<script lang="ts">
import { computed, defineComponent, h, unref } from 'vue';

import { classNames, convertToUnit, omit } from '@components/utils';

import {
  makeAlignProps,
  makeInvertColor,
  makeDirectionProps,
  makeGroupProps,
  makeIconProps,
  makeSlideGroupProps,
  makeTagProps,
  makeThemeProps,
  provideTheme,
  useInvertColor,
  useProxyModel,
  useRender
} from '../../composable';
import { CdsSlideGroup } from '../CdsSlideGroup';

import { CdsTabsSymbol } from './shared';
import { parseItems } from './utils';

import { CdsTab } from './';

import type { TCdsTabItem } from './utils';
import type { TCdsIconsName } from '@central-design-system/icons';
import type { PropType } from 'vue';

export const componentName = 'CdsTabs';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Список элементов
     */
    items: {
      type: Array as PropType<readonly TCdsTabItem[]>,
      default: () => []
    },
    /**
     * Занимать все доступное пространство
     */
    grow: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    /**
     * Доступная высота элементов
     */
    height: {
      type: [Number, String] as PropType<number | string>,
      default: 40
    },
    /**
     * Задавать высоту всех элементов по самому высокому элементу
     */
    stacked: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    ...makeTagProps(),
    ...makeAlignProps(),
    ...makeDirectionProps(),
    ...makeSlideGroupProps(),
    ...makeInvertColor(),
    ...makeThemeProps(),
    ...makeGroupProps({
      required: 'force' as const
    }),
    ...makeIconProps({
      prependIcon: 'chevron-left' as TCdsIconsName,
      appendIcon: 'chevron-right' as TCdsIconsName
    })
  },
  emits: {
    'update:modelValue': (value: unknown) => true
  },
  setup(props, { slots }) {
    const model = useProxyModel(props, 'modelValue');
    const parsedItems = computed(() => parseItems(props.items));
    const { themeClasses } = provideTheme(props);
    const { invertColorClasses } = useInvertColor(props, componentName);

    useRender(() => {
      const slideGroupProps = omit(props, ['align', 'grow', 'height', 'stacked', 'items']);

      return h(
        CdsSlideGroup,
        {
          ...slideGroupProps,
          modelValue: unref(model),
          role: 'tablist',
          class: classNames(
            'cds-tabs',
            `cds-tabs--${props.direction}`,
            `cds-tabs--align-${props.align}`,
            {
              'cds-tabs--grow': props.grow,
              'cds-tabs--stacked': props.stacked
            },
            unref(invertColorClasses),
            unref(themeClasses)
          ),
          style: {
            height: convertToUnit(props.height)
          },
          symbol: CdsTabsSymbol,
          'onUpdate:modelValue': (value: unknown) => {
            model.value = value;
          }
        },
        {
          default: () =>
            slots.default
              ? slots.default?.()
              : unref(parsedItems).map((item) =>
                  h(CdsTab, { ...item, key: item.title, invertColor: props.invertColor })
                )
        }
      );
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsTabs';
</style>
